import React from 'react'
import { useNavigate } from 'react-router-dom'
import RemarksSubNav from './components/RemarksSubNav'
import RemarksStudentPicker from './components/RemarksStudentPicker'
import AppDateInput from '../../components/AppDateInput'
import AppCombobox from '../../components/AppCombobox'
import AppFormFooterBar from '../../components/AppFormFooterBar'
import useRemarksEntry from './hooks/useRemarksEntry'

export default function RemarksEntryScreen() {
  const navigate = useNavigate()
  const { state, actions } = useRemarksEntry()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Top Canopy: Header & SubNav ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Student Discipline &amp; Feedback
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Student Remark' : 'Create Student Remark'}
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            {state.isEditMode
              ? 'Update observation notes and faculty feedback for this student.'
              : 'Tag behavioral observations, achievements, or disciplinary warnings across target students.'}
          </p>
        </div>

      </section>

      {/* ── Section 1: Remark Details & Observation Notes ── */}
      <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 sm:p-8 shadow-xs">
        <div className="flex items-center gap-2 text-primary font-bold text-sm border-b border-outline-variant/15 pb-4 mb-6">
          <span className="material-symbols-outlined text-base">rate_review</span>
          <span>Remark Details &amp; Observation</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Remark Date */}
          <div>
            <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant block mb-1.5">
              Remark Date <span className="text-rose-500">*</span>
            </label>
            <AppDateInput
              value={state.remarkDate}
              onChange={actions.setRemarkDate}
              placeholder="dd-mm-yyyy"
              inputClassName="h-10 text-xs font-bold"
            />
          </div>

          {/* Remark Category (Shared AppCombobox) */}
          <div>
            <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant block mb-1.5">
              Remark Category <span className="text-rose-500">*</span>
            </label>
            <AppCombobox
              options={state.options.remarkTypes}
              value={state.remarkName}
              onChange={actions.setRemarkName}
              placeholder="Select Remark Category..."
              triggerClassName="h-10 w-full bg-surface-container-low text-xs font-bold text-primary"
            />
          </div>

          {/* Remarked By / Faculty (Shared AppCombobox) */}
          <div>
            <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant block mb-1.5">
              Remarked By <span className="text-rose-500">*</span>
            </label>
            <AppCombobox
              options={state.options.facultyList}
              value={state.remarkedBy}
              onChange={actions.setRemarkedBy}
              placeholder="Select Faculty / Staff..."
              triggerClassName="h-10 w-full bg-surface-container-low text-xs font-bold text-on-surface"
            />
          </div>

          {/* Particulars Note (Full Width) */}
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant block mb-1.5">
              Particulars / Detailed Notes
            </label>
            <textarea
              rows={3}
              placeholder="Enter comprehensive observation remarks, student feedback, or parent discussion notes..."
              value={state.particulars}
              onChange={(e) => actions.setParticulars(e.target.value)}
              className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-xs text-on-surface outline-none focus:border-primary resize-y"
            />
          </div>
        </div>
      </div>

      {/* ── Section 2: Student Target Picker or Single Student Banner ── */}
      {state.isEditMode ? (
        <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">
          <div className="flex items-center gap-2 text-primary font-bold text-sm border-b border-outline-variant/15 pb-4 mb-4">
            <span className="material-symbols-outlined text-base">person</span>
            <span>Target Student Profile</span>
          </div>

          {state.editingStudent && (
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary font-black">
                  {state.editingStudent.rollNo}
                </div>
                <div>
                  <h3 className="font-display text-base font-extrabold text-[#3b82f6]">
                    {state.editingStudent.fullName}
                  </h3>
                  <p className="text-xs text-on-surface-variant">
                    {state.editingStudent.courseName} • {state.editingStudent.batchNames}
                  </p>
                </div>
              </div>

              <div className="text-right text-xs">
                <p className="font-bold text-on-surface">{state.editingStudent.branchName}</p>
                <p className="text-[11px] text-on-surface-variant">
                  {state.editingStudent.instituteName}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <RemarksStudentPicker
          students={state.students}
          selectedIds={state.selectedStudentIds}
          onToggleStudent={actions.handleToggleStudent}
          onToggleAll={actions.handleToggleAll}
          companyFilter={state.companyFilter}
          branchFilter={state.branchFilter}
          courseFilter={state.courseFilter}
          academicYearFilter={state.academicYearFilter}
          batchFilter={state.batchFilter}
          subjectFilter={state.subjectFilter}
          searchText={state.studentSearchText}
          searchField={state.studentSearchField}
          setCompanyFilter={actions.setCompanyFilter}
          setBranchFilter={actions.setBranchFilter}
          setCourseFilter={actions.setCourseFilter}
          setAcademicYearFilter={actions.setAcademicYearFilter}
          setBatchFilter={actions.setBatchFilter}
          setSubjectFilter={actions.setSubjectFilter}
          setSearchText={actions.setStudentSearchText}
          setSearchField={actions.setStudentSearchField}
          onReset={actions.handleResetScopeFilters}
          options={state.options}
        />
      )}

      {/* ── Bottom Fixed Save Bar ── */}
      <AppFormFooterBar
        onSave={() => actions.handleSave(false)}
        onCancel={() => navigate('/remarks/list')}
        isSaving={state.isSaving}
        saveText={state.isEditMode ? 'Update Remark' : 'Save Remark'}
        cancelText="Cancel"
        actionsSlot={
          !state.isEditMode && (
            <button
              type="button"
              disabled={state.isSaving || state.selectedStudentIds.length === 0}
              onClick={() => actions.handleSave(true)}
              className="flex items-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">send_and_archive</span>
              <span>Save &amp; Send Parent Alerts</span>
            </button>
          )
        }
      />
    </div>
  )
}
